/**
 * admin/makua/AdminSellers.js — third-party sellers (spec 05).
 *
 * Partners who book retreats for their clients. Each has a sign-in (their
 * email, with the website's emailed code), a referral link, commission
 * terms, and a setting for how much of each booking's payment they see.
 * Staff keep rooms and medical clearance; sellers never see either.
 */
import { useState } from "react";
import toast from "react-hot-toast";
import AdminLayout from "../AdminLayout";
import { sellersAPI } from "../../services/api";
import { useCurrentAdmin, hasPermission } from "../permissions";
import { useLoad, Field, Modal, Badge, StatusBadge, money, range, fmtStamp } from "./ops";
import "./ops.css";

const TERMS = { percent: "Percentage of each booking", per_guest: "Amount per guest", none: "No commission" };
const SEES = {
  none: "Nothing about payments",
  status: "Payment status (unpaid, deposit paid, paid in full)",
  amounts: "Payment status and amounts",
};
const BLANK = { name: "", email: "", company: "", phone: "", commission_mode: "percent", commission_value: 10, payment_visibility: "status", notes: "", active: true };

export default function AdminSellers() {
  const { admin } = useCurrentAdmin();
  const canEdit = hasPermission(admin, "sellers.edit");
  const { data, loading, error, reload } = useLoad(() => sellersAPI.list(), []);
  const [editing, setEditing] = useState(null);   // BLANK for a new one, or a seller
  const [open, setOpen] = useState(null);
  const [range_, setRange] = useState({ from: "", to: "" });
  const list = data?.sellers || [];

  return (
    <AdminLayout pageTitle="Sellers" pageSubtitle="Partners who book for their clients: their sign-in, link, commission and bookings." permission="sellers.view" feature="reservations">
      <div className="ops">
        <div className="ops-bar">
          <Field label="Arrivals from"><input type="date" value={range_.from} onChange={(e) => setRange({ ...range_, from: e.target.value })} /></Field>
          <Field label="to"><input type="date" value={range_.to} onChange={(e) => setRange({ ...range_, to: e.target.value })} /></Field>
          <a className="ops-btn" href={sellersAPI.report(range_)}>Commission report (CSV)</a>
          <span className="ops-spacer" />
          {canEdit && <button className="ops-btn ops-btn--primary" onClick={() => setEditing(BLANK)}>Add seller</button>}
        </div>

        {error && <p className="ops-error">{error}</p>}
        <div className="ops-table-wrap">
          <table className="ops-table">
            <thead>
              <tr><th>Seller</th><th>Sign-in</th><th>Commission</th><th>They see</th><th className="num">Live bookings</th><th className="num">Earned</th><th className="num">Pending</th><th>Status</th></tr>
            </thead>
            <tbody>
              {loading && !list.length && <tr><td colSpan={8} className="ops-empty">Loading…</td></tr>}
              {!loading && !list.length && (
                <tr><td colSpan={8} className="ops-empty">No sellers yet. Add a partner to give them a sign-in, a referral link and commission.</td></tr>
              )}
              {list.map((s) => (
                <tr key={s.id}>
                  <td><button className="ops-row-btn" onClick={() => setOpen(s.id)}>{s.name}</button>{s.company && <span className="ops-sub">{s.company}</span>}</td>
                  <td>{s.email}<span className="ops-sub">{s.referral_code}</span></td>
                  <td>{s.terms}</td>
                  <td>{s.payment_visibility === "none" ? "No payments" : s.payment_visibility === "status" ? "Payment status" : "Status and amounts"}</td>
                  <td className="num">{s.totals.live}<span className="ops-sub">{s.totals.guests} guests</span></td>
                  <td className="num">{money(s.totals.earned)}</td>
                  <td className="num">{money(s.totals.pending)}</td>
                  <td>{s.active ? <Badge tone="green">Active</Badge> : <Badge>Paused</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <SellerForm seller={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {open && <SellerDetail id={open} canEdit={canEdit} onClose={() => setOpen(null)} onEdit={(s) => { setOpen(null); setEditing(s); }} />}
    </AdminLayout>
  );
}

function SellerForm({ seller, onClose, onSaved }) {
  const isNew = !seller.id;
  const [f, setF] = useState({ ...BLANK, ...seller });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: f.name, email: f.email, company: f.company, phone: f.phone, notes: f.notes, active: f.active,
        commission_mode: f.commission_mode, commission_value: f.commission_mode === "none" ? 0 : Number(f.commission_value) || 0,
        payment_visibility: f.payment_visibility,
      };
      const res = isNew ? await sellersAPI.create(payload) : await sellersAPI.update(seller.id, payload);
      toast.success(res.message || "Saved.");
      onSaved();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={isNew ? "Add a seller" : `Edit ${seller.name}`} onClose={onClose}>
      <div className="ops-grid ops-grid--2">
        <Field label="Name"><input value={f.name} onChange={set("name")} autoFocus /></Field>
        <Field label="Company (optional)"><input value={f.company || ""} onChange={set("company")} /></Field>
        <Field label="Email" hint="They sign in on the website with this, using the emailed code."><input type="email" value={f.email || ""} onChange={set("email")} /></Field>
        <Field label="Phone (optional)"><input value={f.phone || ""} onChange={set("phone")} /></Field>
        <Field label="Commission">
          <select value={f.commission_mode} onChange={set("commission_mode")}>
            {Object.entries(TERMS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        {f.commission_mode !== "none" && (
          <Field label={f.commission_mode === "percent" ? "Percent of the booking total" : "Amount per guest (USD)"}>
            <input type="number" min="0" max={f.commission_mode === "percent" ? 100 : undefined} step="0.01" value={f.commission_value} onChange={set("commission_value")} />
          </Field>
        )}
      </div>
      <Field label="What they see about payments">
        <select value={f.payment_visibility} onChange={set("payment_visibility")}>
          {Object.entries(SEES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </Field>
      <Field label="Notes for staff (sellers never see these)"><textarea rows={3} value={f.notes || ""} onChange={set("notes")} /></Field>
      {!isNew && <label className="ops-check"><input type="checkbox" checked={!!f.active} onChange={set("active")} /> Active: can sign in, book, and earn through their link</label>}
      <div className="ops-actions" style={{ justifyContent: "flex-end", marginTop: "0.75rem" }}>
        <button className="ops-btn" onClick={onClose}>Cancel</button>
        <button className="ops-btn ops-btn--primary" disabled={busy || !f.name || !f.email} onClick={save}>{busy ? "Saving…" : isNew ? "Add seller" : "Save"}</button>
      </div>
    </Modal>
  );
}

function SellerDetail({ id, canEdit, onClose, onEdit }) {
  const { data, loading, error } = useLoad(() => sellersAPI.get(id), [id]);
  const s = data?.seller;
  const copy = (text) => { try { navigator.clipboard.writeText(text); toast.success("Copied."); } catch (_) { /* no clipboard */ } };
  return (
    <>
      <div className="ops-drawer-scrim" onClick={onClose} />
      <aside className="ops-drawer" aria-label="Seller">
        <div className="ops-drawer-head">
          <div>
            <h2>{s ? s.name : "Seller"}</h2>
            {s && <p className="ops-muted" style={{ margin: "0.2rem 0 0" }}>{[s.company, s.email].filter(Boolean).join(" · ")}</p>}
          </div>
          <button type="button" className="ops-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="ops-drawer-body">
          {loading && !s && <p className="ops-muted">Loading…</p>}
          {error && <p className="ops-error">{error}</p>}
          {s && (
            <>
              <div className="ops-card">
                <div className="ops-card-head">
                  <h3>Terms</h3>
                  {canEdit && <button className="ops-btn ops-btn--sm" onClick={() => onEdit(s)}>Edit</button>}
                </div>
                <dl className="ops-kv">
                  <dt>Commission</dt><dd>{s.terms}</dd>
                  <dt>They see</dt><dd>{SEES[s.payment_visibility]}</dd>
                  <dt>Referral link</dt>
                  <dd><code>{s.referralLink}</code> <button className="ops-btn ops-btn--sm" onClick={() => copy(s.referralLink)}>Copy</button></dd>
                  <dt>Earned</dt><dd><b>{money(s.totals.earned)}</b> · {money(s.totals.pending)} pending</dd>
                  {s.notes && <><dt>Notes</dt><dd>{s.notes}</dd></>}
                </dl>
              </div>
              <div className="ops-card">
                <h3>Bookings</h3>
                {data.reservations.length === 0 ? <p className="ops-note">None yet.</p> : (
                  <div className="ops-table-wrap">
                    <table className="ops-table">
                      <thead><tr><th>Reference</th><th>Client</th><th>Stay</th><th>Status</th><th className="num">Total</th><th className="num">Commission</th></tr></thead>
                      <tbody>
                        {data.reservations.map((r) => (
                          <tr key={r.ref}>
                            <td>{r.ref}<span className="ops-sub">{fmtStamp(r.created)} · {r.viaLink ? "through their link" : "booked by them"}</span></td>
                            <td>{r.client}<span className="ops-sub">{r.guests} {r.guests === 1 ? "guest" : "guests"}</span></td>
                            <td>{r.retreat}<span className="ops-sub">{range(r.arrive, r.depart)}</span></td>
                            <td><StatusBadge status={r.status} /><span className="ops-sub">{r.payment}</span></td>
                            <td className="num">{money(r.total)}</td>
                            <td className="num">{money(r.commission)}<span className="ops-sub">{r.commissionState === "none" ? "—" : r.commissionState}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
